// src/pages/Member/Profile/CreatePartner/CreatePartner.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/axios";
import i18n from "@/i18n";
import { toast } from "react-toastify";

import cls from "./CreatePartner.module.scss";

// Enums
import { GENDERS } from "@/enums/MemberEnums";
import {
  RELATIONSHIP_TYPES,
  RELATIONSHIP_STATUSES,
} from "@/enums/PartnerEnums";

// Same processed subpath pattern as payment / real_image
const DEFAULT_PROCESSED_SUBPATH = "images/poster.webp";

const CreatePartner = () => {
  const navigate = useNavigate();

  // Relationship info
  const [partner, setPartner] = useState({
    relationshipType: "",
    relationshipStatus: "",
  });

  // Personal info required by backend
  const [partnerPersonal, setPartnerPersonal] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    birthday: "",
    mobilePhone: "",
    realImageKey: "",
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Required fields for createPartnerProfile
  const isSubmitDisabled =
    submitting ||
    uploading ||
    !partner.relationshipType ||
    !partner.relationshipStatus ||
    !partnerPersonal.firstName ||
    !partnerPersonal.lastName ||
    !partnerPersonal.mobilePhone ||
    !partnerPersonal.gender ||
    !partnerPersonal.birthday ||
    !partnerPersonal.realImageKey;

  // -------- Handlers --------

  const handlePartnerChange = (e) => {
    const { name, value } = e.target;
    setPartner((prev) => ({ ...prev, [name]: value }));
  };

  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    setPartnerPersonal((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError("");
    setUploading(true);

    try {
      const ext = file.name.split(".").pop() || "jpg";

      // 1) Get presigned URL + processedBaseKey
      const presignRes = await api.post(
        "/v1/member/profile/partners/presign-real-image",
        {
          ext,
          contentType: file.type || "application/octet-stream",
        }
      );

      const { uploadUrl, processedBaseKey } = presignRes.data || {};
      if (!uploadUrl || !processedBaseKey) {
        throw new Error("Invalid presign response");
      }

      // 2) Upload file directly to S3
      await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      // 3) Use same processed subpath convention as payment / real_image
      const processedKey = `${processedBaseKey}/${DEFAULT_PROCESSED_SUBPATH}`;

      setPartnerPersonal((prev) => ({
        ...prev,
        realImageKey: processedKey,
      }));

      toast.success(
        i18n.t("memberProfileCreatePartner.upload.successToast", {
          defaultValue: "Partner real image uploaded.",
        })
      );
    } catch (err) {
      console.error("Partner real image upload error", err);
      setUploadError(
        i18n.t("memberProfileCreatePartner.upload.error", {
          defaultValue:
            "Upload failed. Please try again or choose a different image.",
        })
      );
      toast.error(
        i18n.t("memberProfileCreatePartner.upload.errorToast", {
          defaultValue: "Could not upload image.",
        })
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitDisabled) return;

    setSubmitting(true);

    try {
      // Flat payload for createPartnerProfile controller
      const payload = {
        relationshipType: partner.relationshipType,
        relationshipStatus: partner.relationshipStatus,
        birthday: partnerPersonal.birthday,
        gender: partnerPersonal.gender,
        realImageKey: partnerPersonal.realImageKey,
        firstName: partnerPersonal.firstName,
        lastName: partnerPersonal.lastName,
        mobilePhone: partnerPersonal.mobilePhone,
      };

      const res = await api.post("/v1/member/profile/partners", payload);

      toast.success(
        res?.data?.message ||
          i18n.t("memberProfileCreatePartner.form.success", {
            defaultValue: "Partner profile created.",
          })
      );

      navigate("/member/my-profile");
    } catch (err) {
      console.error("CreatePartner submit error", err);
      const msg =
        err?.response?.data?.message ||
        i18n.t("memberProfileCreatePartner.form.error", {
          defaultValue:
            "Something went wrong when creating your partner profile.",
        });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // -------- Render helpers --------

  const renderEnumOptions = (prefixKey, values) =>
    values.map((val) => (
      <option key={val} value={val}>
        {i18n.t(`${prefixKey}.${val}`)}
      </option>
    ));

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <h1 className={cls.title}>
          {i18n.t("memberProfileCreatePartner.title")}
        </h1>
        <p className={cls.subtitle}>
          {i18n.t("memberProfileCreatePartner.subtitle")}
        </p>
      </header>

      <div className={cls.layout}>
        {/* Image / Left column */}
        <section className={cls.imagePanel}>
          <div className={cls.imageCard}>
            <h2 className={cls.sectionTitle}>
              {i18n.t("memberProfileCreatePartner.upload.title")}
            </h2>
            <p className={cls.sectionHelp}>
              {i18n.t("memberProfileCreatePartner.upload.help")}
            </p>

            <label className={cls.uploadButton}>
              <span>
                {uploading
                  ? i18n.t("memberProfileCreatePartner.upload.uploading")
                  : i18n.t("memberProfileCreatePartner.upload.button")}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={uploading}
              />
            </label>

            {partnerPersonal.realImageKey && !uploading && (
              <p className={cls.uploadStatus}>
                {i18n.t("memberProfileCreatePartner.upload.ready")}
              </p>
            )}

            {uploadError && <p className={cls.errorText}>{uploadError}</p>}

            <p className={cls.uploadNote}>
              {i18n.t("memberProfileCreatePartner.upload.note")}
            </p>
          </div>
        </section>

        {/* Form / Right column */}
        <section className={cls.formPanel}>
          <form className={cls.form} onSubmit={handleSubmit}>
            {/* Relationship section */}
            <div className={cls.card}>
              <h2 className={cls.sectionTitle}>
                {i18n.t("memberProfileCreatePartner.sections.relationship")}
              </h2>
              <div className={cls.fieldRow}>
                <label className={cls.label}>
                  {i18n.t(
                    "memberProfileCreatePartner.fields.relationshipType.label"
                  )}
                  <select
                    name="relationshipType"
                    value={partner.relationshipType}
                    onChange={handlePartnerChange}
                    className={cls.select}
                  >
                    <option value="">
                      {i18n.t(
                        "memberProfileCreatePartner.fields.relationshipType.placeholder"
                      )}
                    </option>
                    {renderEnumOptions(
                      "memberProfileCreatePartner.enums.relationshipTypes",
                      RELATIONSHIP_TYPES
                    )}
                  </select>
                </label>
              </div>

              <div className={cls.fieldRow}>
                <label className={cls.label}>
                  {i18n.t(
                    "memberProfileCreatePartner.fields.relationshipStatus.label"
                  )}
                  <select
                    name="relationshipStatus"
                    value={partner.relationshipStatus}
                    onChange={handlePartnerChange}
                    className={cls.select}
                  >
                    <option value="">
                      {i18n.t(
                        "memberProfileCreatePartner.fields.relationshipStatus.placeholder"
                      )}
                    </option>
                    {renderEnumOptions(
                      "memberProfileCreatePartner.enums.relationshipStatuses",
                      RELATIONSHIP_STATUSES
                    )}
                  </select>
                </label>
              </div>
            </div>

            {/* Personal section */}
            <div className={cls.card}>
              <h2 className={cls.sectionTitle}>
                {i18n.t("memberProfileCreatePartner.sections.personal")}
              </h2>

              <div className={cls.fieldGridTwo}>
                <label className={cls.label}>
                  {i18n.t("memberProfileCreatePartner.fields.firstName.label")}
                  <input
                    type="text"
                    name="firstName"
                    value={partnerPersonal.firstName}
                    onChange={handlePersonalChange}
                    className={cls.input}
                    placeholder={i18n.t(
                      "memberProfileCreatePartner.fields.firstName.placeholder"
                    )}
                  />
                </label>
                <label className={cls.label}>
                  {i18n.t("memberProfileCreatePartner.fields.lastName.label")}
                  <input
                    type="text"
                    name="lastName"
                    value={partnerPersonal.lastName}
                    onChange={handlePersonalChange}
                    className={cls.input}
                    placeholder={i18n.t(
                      "memberProfileCreatePartner.fields.lastName.placeholder"
                    )}
                  />
                </label>
              </div>

              <div className={cls.fieldGridTwo}>
                <label className={cls.label}>
                  {i18n.t("memberProfileCreatePartner.fields.birthday.label", {
                    defaultValue: "Birthday",
                  })}
                  <input
                    type="date"
                    name="birthday"
                    value={partnerPersonal.birthday}
                    onChange={handlePersonalChange}
                    className={cls.input}
                  />
                </label>
                <label className={cls.label}>
                  {i18n.t(
                    "memberProfileCreatePartner.fields.mobilePhone.label",
                    { defaultValue: "Mobile number" }
                  )}
                  <input
                    type="tel"
                    name="mobilePhone"
                    value={partnerPersonal.mobilePhone}
                    onChange={handlePersonalChange}
                    className={cls.input}
                    placeholder={i18n.t(
                      "memberProfileCreatePartner.fields.mobilePhone.placeholder",
                      { defaultValue: "+1 587 ..." }
                    )}
                  />
                </label>
              </div>

              <div className={cls.fieldRow}>
                <label className={cls.label}>
                  {i18n.t("memberProfileCreatePartner.fields.gender.label")}
                  <select
                    name="gender"
                    value={partnerPersonal.gender}
                    onChange={handlePersonalChange}
                    className={cls.select}
                  >
                    <option value="">
                      {i18n.t(
                        "memberProfileCreatePartner.fields.gender.placeholder"
                      )}
                    </option>
                    {renderEnumOptions(
                      "memberProfileCreatePartner.enums.genders",
                      GENDERS
                    )}
                  </select>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryButton}
                onClick={() => navigate(-1)}
              >
                {i18n.t("memberProfileCreatePartner.actions.back")}
              </button>
              <button
                type="submit"
                className={`${cls.primaryButton} ${
                  isSubmitDisabled ? cls.primaryButtonDisabled : ""
                }`}
                disabled={isSubmitDisabled}
              >
                {submitting
                  ? i18n.t("memberProfileCreatePartner.actions.submitting")
                  : i18n.t("memberProfileCreatePartner.actions.submit")}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default CreatePartner;
